import CheckBoxIcon from "@mui/icons-material/Check";
import { Grid, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { DisplayOnlyField, DisplayOnlyFieldList } from "components/Forms/DisplayOnlyField";
import { getFullName } from "encamp-shared/src/utils/name";
import { prettyPrintPhone } from "encamp-shared/src/utils/prettyPrintPhone";
import { AssignmentType, PhoneType } from "generated-graphql/graphql";
import { DateTime } from "luxon";
import { useMemo } from "react";

import { StepperPageHeader } from "../StepperPageHeader";
import { useReport } from "../useReport";

export type FormattedReviewer = {
  personId: string;
  reviewComplete: boolean;
  reviewedAt?: string;
  reviewedBy: string;
  reviewerPhones: string[];
  reviewerEmail?: string | null;
};

export type FormattedVerifier = {
  verifiedAt: string;
  verifiedBy: string;
  verifiedByTitle: string;
  verifierPhone?: string | null;
  verifierEmail?: string | null;
};

export type RawReviewer = {
  person: {
    id: string;
    email?: string | null;
    phones?: Array<{ number?: string | null; type?: PhoneType | null }> | null;
    first?: string | null;
    last?: string | null;
  };
  isComplete?: boolean | null;
  completedAt?: string | null;
  updatedAt?: string | null;
};

export type RawVerifierData = {
  verifiedAt?: string | null;
  verifierFullName?: string | null;
  verifierTitle?: string | null;
  verifierPhone?: string | null;
  verifierEmail?: string | null;
  verifier?: {
    first?: string | null;
    last?: string | null;
    title?: string | null;
  } | null;
};

type ColumnDefinition = {
  id: string;
  label: string;
  width: number;
  isList?: boolean;
  formatter?: (value: any) => string;
};

export function ReviewersAndVerifier() {
  const { data } = useReport();

  // Format the reviewers, if there are multiple reviewers with the same review date, keep the first one
  const reviewers: FormattedReviewer[] = useMemo(() => {
    const rawReviewers =
      data?.tierIIReport.reviewers?.filter((r) => r.assignmentType === AssignmentType.Reviewer) ??
      [];

    const formattedReviewers = rawReviewers.map(formatReviewer);
    return deduplicateReviewers(formattedReviewers);
  }, [data]);

  // Format the verifier
  const verifier = useMemo(() => {
    return formatVerifier(data?.tierIIReport ?? {});
  }, [data]);

  const hasReviewers = reviewers.length > 0;

  const reviewerColumns: ColumnDefinition[] = [
    { id: "reviewedAt", label: "Reviewed on", width: 2.5 },
    { id: "reviewedBy", label: "Name", width: 2.5 },
    { id: "reviewerPhones", label: "Phone", width: 2.5, isList: true },
    { id: "reviewerEmail", label: "Email", width: 2.5 },
  ];

  const verifierColumns: ColumnDefinition[] = [
    {
      id: "verifiedAt",
      label: "Verified on",
      width: 2,
      formatter: (value) => DateTime.fromISO(value ?? "", { zone: "utc" }).toFormat("MM/dd/yyyy"),
    },
    { id: "verifiedBy", label: "Name", width: 2.2 },
    { id: "verifiedByTitle", label: "Title", width: 2 },
    { id: "verifierPhone", label: "Phone", width: 2.2 },
    { id: "verifierEmail", label: "Email", width: 2.6 },
  ];

  return (
    <Stack>
      <StepperPageHeader header="Reviewers & Verifiers" />
      {hasReviewers && (
        <TableDisplay
          title="Reviewers"
          items={reviewers}
          columns={reviewerColumns}
          checkmarkField="reviewComplete"
          checkmarkDisplayColumn="reviewedAt"
        />
      )}
      {verifier && (
        <TableDisplay
          title="Verifier"
          items={[verifier]}
          columns={verifierColumns}
          hasCheckmark={true}
          checkmarkDisplayColumn="verifiedAt"
        />
      )}
      {!hasReviewers && !verifier && (
        <Typography color="text.secondary">
          No reviewers or verifier are assigned to this report.
        </Typography>
      )}
    </Stack>
  );
}

const HeaderCell = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: "0.875rem", // 14px
  padding: theme.spacing(1),
  borderBottom: "1px solid",
  borderBottomColor: theme.palette.grey[200],
  minHeight: theme.spacing(6),
  display: "flex",
  alignItems: "center",
}));

const ContentCell = styled(Stack)(({ theme }) => ({
  fontSize: "0.875rem", // 14px
  padding: theme.spacing(1),
}));

function CompletedCell({ completed }: { completed: boolean }) {
  return <ContentCell>{completed && <CheckBoxIcon />}</ContentCell>;
}

type TableDisplayProps = {
  title: string;
  items: any[];
  columns: ColumnDefinition[];
  checkmarkField?: string;
  checkmarkDisplayColumn?: string;
  hasCheckmark?: boolean;
};

function TableDisplay({
  title,
  items,
  columns,
  checkmarkField = "",
  checkmarkDisplayColumn = "",
}: TableDisplayProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  return (
    <Stack sx={{ mb: theme.spacing(8) }}>
      <Typography sx={{ mb: theme.spacing(3) }} color="text.secondary">
        {title}
      </Typography>
      {isMobile ? (
        <MobileView
          items={items}
          columns={columns}
          checkmarkField={checkmarkField}
          checkmarkDisplayColumn={checkmarkDisplayColumn}
        />
      ) : (
        <DesktopView items={items} columns={columns} checkmarkField={checkmarkField} />
      )}
    </Stack>
  );
}

type MobileViewProps = {
  items: any[];
  columns: ColumnDefinition[];
  checkmarkField: string;
  checkmarkDisplayColumn: string;
};

function MobileView({ items, columns, checkmarkField, checkmarkDisplayColumn }: MobileViewProps) {
  const theme = useTheme();

  return (
    <Stack spacing={4}>
      {items.map((item, idx) => (
        <Stack key={idx} spacing={1} sx={{ marginBottom: theme.spacing(4) }}>
          {columns.map((column) => (
            <Stack key={column.id} spacing={0.5}>
              {column.isList ? (
                <DisplayOnlyFieldList small label={column.label} items={item[column.id] || []} />
              ) : (
                <DisplayOnlyField
                  small
                  label={column.label}
                  text={
                    column.formatter ? column.formatter(item[column.id]) : item[column.id] || ""
                  }
                  renderValue={
                    column.id === checkmarkDisplayColumn
                      ? (value) => (
                          <Stack direction="row" alignItems="center">
                            {value}
                            {item[checkmarkField] && (
                              <CheckBoxIcon sx={{ ml: 2, height: 18, width: 18 }} />
                            )}
                          </Stack>
                        )
                      : undefined
                  }
                />
              )}
            </Stack>
          ))}
        </Stack>
      ))}
    </Stack>
  );
}

type DesktopViewProps = {
  items: any[];
  columns: ColumnDefinition[];
  checkmarkField: string;
};

function DesktopView({ items, columns, checkmarkField }: DesktopViewProps) {
  return (
    <Stack>
      <Grid container>
        <Grid size={{ xs: 1 }}>
          <HeaderCell />
        </Grid>
        {columns.map((column) => (
          <Grid size={{ xs: column.width }} key={column.id}>
            <HeaderCell>{column.label}</HeaderCell>
          </Grid>
        ))}
      </Grid>
      {items.map((item, idx) => (
        <Grid container key={idx}>
          <Grid size={{ xs: 1 }}>
            <CompletedCell
              completed={item[checkmarkField] !== undefined ? item[checkmarkField] : true}
            />
          </Grid>
          {columns.map((column) => (
            <Grid size={{ xs: column.width }} key={column.id}>
              <ContentCell>
                {column.isList ? (
                  <DisplayOnlyFieldList small items={item[column.id] || []} />
                ) : (
                  <DisplayOnlyField
                    small
                    text={
                      column.formatter ? column.formatter(item[column.id]) : item[column.id] || ""
                    }
                  />
                )}
              </ContentCell>
            </Grid>
          ))}
        </Grid>
      ))}
    </Stack>
  );
}

export function formatReviewer(reviewer: RawReviewer): FormattedReviewer {
  return {
    personId: reviewer.person.id,
    reviewComplete: reviewer.isComplete ?? false,
    reviewedAt: reviewer.completedAt
      ? DateTime.fromISO(reviewer.completedAt, { zone: "utc" }).toFormat("MM/dd/yyyy")
      : undefined,
    reviewedBy: getFullName(reviewer.person),
    reviewerPhones: reviewer.person.phones?.map((phone) => prettyPrintPhone(phone)) ?? [],
    reviewerEmail: reviewer.person.email,
  };
}

export function deduplicateReviewers(reviewers: FormattedReviewer[]): FormattedReviewer[] {
  return reviewers.reduce((acc, current) => {
    const existingIndex = acc.findIndex((existing) => existing.personId === current.personId);
    if (existingIndex === -1) {
      acc.push(current);
    } else {
      // Keep the one with the most recent review date
      const existingDate = acc[existingIndex].reviewedAt ?? "";
      const currentDate = current.reviewedAt ?? "";
      if (currentDate > existingDate) {
        acc[existingIndex] = current;
      }
    }
    return acc;
  }, [] as FormattedReviewer[]);
}

export function formatVerifier(data: RawVerifierData): FormattedVerifier | undefined {
  const verifiedAt = data.verifiedAt;
  if (!verifiedAt) return undefined;

  return {
    verifiedAt,
    verifiedBy: data.verifierFullName || getFullName(data.verifier),
    verifiedByTitle: data.verifierTitle || data.verifier?.title || "",
    verifierPhone: data.verifierPhone,
    verifierEmail: data.verifierEmail,
  };
}
